<template>
    <div class="task-center">
        <ElPopover
            width="300"
            placement="bottom"
            :visible="taskCenterStore.isTaskCenterOpen"
            popper-class="task-center-popover"
            :teleported="false"
        >
            <template #reference>
                <ElLink :underline="false" class="task-center--entry" @click="onOpenTaskCenter">
                    <i class="task-center-entry--icon iconfont icon-renwuzhongxin"></i>
                    <div v-if="taskCenterStore.showNotice" class="task-center-entry--tag"></div>
                </ElLink>
            </template>
            <template #default>
                <div>
                    <div class="task-center--header">
                        <template v-if="!openSearch">
                            <div class="task-center--title">任务中心</div>
                            <div class="task-center--operations">
                                <ElTooltip effect="dark" content="搜索文件名" placement="top">
                                    <ElButton link class="search" @click="onOpenSearch">
                                        <i class="iconfont icon-search"></i>
                                    </ElButton>
                                </ElTooltip>
                                <ElTooltip effect="dark" content="一键清除所有" placement="top">
                                    <ElButton link class="clear" @click="taskCenterStore.remoteClearTaskList">
                                        <i class="iconfont icon-a-yijianshanchuicon"></i>
                                    </ElButton>
                                </ElTooltip>
                                <ElButton link class="close" @click="onCloseTaskCenter">
                                    <i class="iconfont icon-close1"></i>
                                </ElButton>
                            </div>
                        </template>
                        <template v-else>
                            <div class="search-back" @click="onCloseSearch">
                                <i class="iconfont icon-left-arrow"></i>
                            </div>
                            <ElInput
                                v-model="taskCenterStore.searchKeyword"
                                class="search-input"
                                placeholder="请输入文件名搜索"
                                clearable
                                @change="onSearchTask"
                            >
                                <template #prefix>
                                    <i class="iconfont icon-search"></i>
                                </template>
                            </ElInput>
                        </template>
                    </div>
                    <template v-if="taskCenterStore.taskList.length">
                        <div
                            v-infinite-scroll="onScrollGetTaskList"
                            class="task-center--list"
                            :infinite-scroll-disabled="viewMode === ViewModeEnum.Abbreviated"
                            :infinite-scroll-distance="48"
                        >
                            <div
                                v-for="(taskItem, index) in taskCenterStore.taskList"
                                :key="taskItem.id"
                                class="task-item"
                                @mouseenter="() => (activeTaskItemIndex = index)"
                                @mouseleave="() => (activeTaskItemIndex = undefined)"
                            >
                                <div class="task-item--first-line">
                                    <ElTooltip
                                        v-if="taskItem.fileName.length > 22"
                                        effect="dark"
                                        :content="`【${TASK_TYPE.getIntroByCode(taskItem.taskType)}】${taskItem.fileName}`"
                                        placement="top"
                                    >
                                        <div class="task-item--title">
                                            <span>【{{ TASK_TYPE.getIntroByCode(taskItem.taskType) }}】</span>
                                            <span
                                                v-html="highlightKeyword(taskItem.fileName, taskCenterStore.searchKeyword || '')"
                                            ></span>
                                        </div>
                                    </ElTooltip>
                                    <div v-else class="task-item--title">
                                        <span>【{{ TASK_TYPE.getIntroByCode(taskItem.taskType) }}】</span>
                                        <span
                                            v-html="highlightKeyword(taskItem.fileName, taskCenterStore.searchKeyword || '')"
                                        ></span>
                                    </div>
                                    <div
                                        :class="`task-item--status status-${taskItem.status}`"
                                        v-html="getTipsByTaskInfo(taskItem)"
                                    ></div>
                                </div>
                                <div class="task-item--third-line">
                                    <div class="task-item--progress">
                                        <ElProgress
                                            :text-inside="true"
                                            :color="`#2143fc`"
                                            :percentage="100"
                                            :stroke-width="10"
                                            striped
                                            striped-flow
                                            :duration="taskItem.status !== TASK_STATUS.NOT_COMPLETED.code ? 0 : 10"
                                            class="w-full"
                                        />
                                    </div>
                                    <div v-if="taskItem.status !== TASK_STATUS.NOT_COMPLETED.code" class="task-item--operations">
                                        <ElButton
                                            v-show="activeTaskItemIndex === index"
                                            link
                                            class="task-item--del"
                                            @click="onDeleteTask(taskItem.id)"
                                        >
                                            <i class="iconfont icon-delete"></i>
                                        </ElButton>
                                        <ElButton
                                            v-if="taskItem.status === TASK_STATUS.COMPLETED.code"
                                            link
                                            class="operations-item--download"
                                            @click="taskCenterStore.downloadTaskFile(taskItem)"
                                        >
                                            <i class="iconfont icon-xiazaiicon"></i>
                                        </ElButton>
                                    </div>
                                </div>
                                <div class="task-item--fourth-line operate-time">
                                    <div class="operate-time--label">操作时间</div>
                                    <div class="operate-time--value">
                                        {{ dateTimeFormat(taskItem.createdTime) }}
                                    </div>
                                </div>
                            </div>
                            <div v-if="viewMode === ViewModeEnum.Normal" class="list-tips">
                                <div v-if="!taskCenterStore.hasMoreTask" class="list-tips--no-more">没有更多数据～</div>
                                <div v-else class="list-tips--loading">加载中...</div>
                            </div>
                        </div>
                        <div
                            v-if="viewMode === ViewModeEnum.Abbreviated && taskCenterStore.totalTask > 3"
                            class="task-center--load-more"
                            @click="switchViewModeToNormal"
                        >
                            查看更多 <i class="iconfont icon-arrow-right"></i>
                        </div>
                    </template>
                    <div v-else class="empty-container">
                        <img class="empty-img" :src="`${CDN_URL}/ghy-components/empty.png`" alt="暂无数据" />
                        <span class="empty-text">暂无数据</span>
                    </div>
                </div>
            </template>
        </ElPopover>
    </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";
import { ElButton, ElInput, ElLink, ElPopover, ElProgress, ElTooltip } from "element-plus";
import { TASK_STATUS, TASK_TYPE } from "@supplier/meta";
import { CDN_URL } from "@supplier/constants";

import type { TaskCenter } from "@supplier/entity";

import { useTaskCenterStore } from "@/stores/taskCenter";
import { dateTimeFormat } from "@/utils";
import { highlightKeyword } from "@/utils/highlight";

/**
 * 视口模式
 */
enum ViewModeEnum {
    /**
     * 一般模式, 无限列表
     */
    Normal,
    /**
     * 简略模式, 只显示三数据, 点击加载更多后加载更多数据
     */
    Abbreviated
}

const taskCenterStore = useTaskCenterStore();

const activeTaskItemIndex = ref<undefined | number>(undefined);
const openSearch = ref(false);
/**
 * 视口模式
 */
const viewMode = ref(ViewModeEnum.Abbreviated);

/**
 * 根据任务信息获取提示信息
 */
const getTipsByTaskInfo = (taskItem: TaskCenter) => {
    if (taskItem.status === TASK_STATUS.COMPLETED.code) {
        return `${TASK_TYPE.getIntroByCode(taskItem.taskType)}成功`;
    } else if (taskItem.status === TASK_STATUS.NOT_COMPLETED.code) {
        return `${TASK_TYPE.getIntroByCode(taskItem.taskType)}中...`;
    } else {
        return `${TASK_TYPE.getIntroByCode(taskItem.taskType)}失败`;
    }
};

// 定时器
let unReadMsgIntervalId: ReturnType<typeof setInterval> | undefined;

/**
 * 当任务中心开启时, 获取任务列表
 */
const getTaskListWhenOpen = async (keyword?: string) => {
    if (taskCenterStore.isTaskCenterOpen) {
        if (keyword) taskCenterStore.setKeyword(keyword);
        await taskCenterStore.getTaskList(viewMode.value === ViewModeEnum.Abbreviated ? 3 : 10);
    }
};

const onOpenSearch = () => {
    openSearch.value = true;
};

const onCloseSearch = () => {
    taskCenterStore.clearTaskList();
    taskCenterStore.setKeyword("");
    getTaskListWhenOpen();
    openSearch.value = false;
};

const onOpenTaskCenter = () => {
    if (!taskCenterStore.isTaskCenterOpen) {
        taskCenterStore.openTaskCenter(false);
    }
};

const switchViewModeToNormal = async () => {
    taskCenterStore.setLoadMore(true);
    viewMode.value = ViewModeEnum.Normal;
    taskCenterStore.clearTaskList();
    await getTaskListWhenOpen();
};

const onCloseTaskCenter = () => {
    taskCenterStore.setLoadMore(false);
    taskCenterStore.initListPage();
    taskCenterStore.closeTaskCenter();
    viewMode.value = ViewModeEnum.Abbreviated;
    onCloseSearch();
};

const onScrollGetTaskList = async () => {
    if (taskCenterStore.hasMoreTask) {
        // taskCenterStore.listPage += 1;
        await getTaskListWhenOpen();
    }
};

const onSearchTask = async (value: string) => {
    taskCenterStore.clearTaskList();
    await getTaskListWhenOpen(value);
};

// 重新导出
// const onRetryTask = (taskId: number) => {
//     // taskCenterStore.retryTask(taskId);
//     retryingTaskIds.value.push(taskId);
//     setTimeout(() => {
//         retryingTaskIds.value.splice(retryingTaskIds.value.indexOf(taskId), 1);
//     }, 2000);
// };

const onDeleteTask = async (taskId: number) => {
    await taskCenterStore.deleteTask(taskId);
    if (viewMode.value === ViewModeEnum.Abbreviated) {
        taskCenterStore.clearTaskList();
        await getTaskListWhenOpen();
    }
};

onMounted(() => {
    // 拉取未读显示红点
    taskCenterStore.getUnReadMsgNum();
    unReadMsgIntervalId = setInterval(taskCenterStore.getUnReadMsgNum, 30000); // 每隔30秒执行一次

    // 清理定时器
    onBeforeUnmount(() => {
        clearInterval(unReadMsgIntervalId);
    });
});
</script>

<style scoped lang="scss">
@use "./index.scss";
</style>

<style lang="scss">
.highlight-text {
    color: #2143fc !important;
}
</style>
